import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
/**
 * Get color for block type
 * Color-codes blocks by their type for visual distinction
 */ function getBlockColor(type) {
    const typeColors = {
        SectionHeader: '#3b82f6',
        Heading: '#3b82f6',
        Title: '#3b82f6',
        Content: '#6b7280',
        Text: '#6b7280',
        Paragraph: '#6b7280',
        Table: '#10b981',
        List: '#f59e0b',
        ListItem: '#f59e0b',
        Image: '#8b5cf6',
        Figure: '#8b5cf6',
        Footer: '#ef4444',
        Header: '#ef4444',
        PageNumber: '#ef4444'
    };
    return typeColors[type] || '#6b7280'; // default gray
}
/**
 * Block Overlay Component
 * Renders bounding boxes over document blocks with hover and selection interactions
 */ export const BlockOverlay = ({ blocks, pageDimensions, selectedBlockId, highlightedBlockIds, onBlockSelect })=>{
    const [hoveredBlockId, setHoveredBlockId] = useState(null);
    const groupHighlight = !!highlightedBlockIds && highlightedBlockIds.size > 0;
    // When a PWS group is highlighted, draw one solid amber rectangle that
    // wraps every member block on this page (header + body + descendants,
    // including blocks that continue from a prior page). Computed as the
    // axis-aligned union of every member's bounding box, expressed as
    // percentages of page dimensions so CSS scale() can zoom both image and
    // overlay uniformly. The individual per-block borders are then suppressed
    // for the highlighted members — the single union box is the only amber.
    const unionRect = useMemo(()=>{
        if (!groupHighlight) return null;
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        let count = 0;
        for (const block of blocks){
            const isMember = block.id === selectedBlockId || (highlightedBlockIds ? highlightedBlockIds.has(block.id) : false);
            if (!isMember) continue;
            const { x, y, width, height } = block.boundingBox;
            // Defend against shred outputs that emit zero-sized boxes; including
            // them would collapse the union toward the origin.
            if (width <= 0 || height <= 0) continue;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x + width > maxX) maxX = x + width;
            if (y + height > maxY) maxY = y + height;
            count++;
        }
        if (count === 0 || !Number.isFinite(minX)) return null;
        return {
            left: `${minX / pageDimensions.width * 100}%`,
            top: `${minY / pageDimensions.height * 100}%`,
            width: `${(maxX - minX) / pageDimensions.width * 100}%`,
            height: `${(maxY - minY) / pageDimensions.height * 100}%`
        };
    }, [
        groupHighlight,
        highlightedBlockIds,
        selectedBlockId,
        blocks,
        pageDimensions
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "absolute inset-0 pointer-events-none",
        children: [
            blocks.map((block)=>{
                const color = getBlockColor(block.type);
                const isPrimarySelected = block.id === selectedBlockId;
                const isGroupMember = !!groupHighlight && highlightedBlockIds.has(block.id);
                const isSelected = isPrimarySelected || isGroupMember;
                const isHovered = block.id === hoveredBlockId;
                const hasActiveSelection = !!selectedBlockId || groupHighlight;
                // Convert pixel coordinates to percentages relative to page dimensions
                const left = `${block.boundingBox.x / pageDimensions.width * 100}%`;
                const top = `${block.boundingBox.y / pageDimensions.height * 100}%`;
                const width = `${block.boundingBox.width / pageDimensions.width * 100}%`;
                const height = `${block.boundingBox.height / pageDimensions.height * 100}%`;
                // When a group is active, suppress the per-block amber treatment for
                // members — the union rectangle handles their highlight. The primary
                // anchor keeps a subtle marker (no border/glow) so we can still see
                // which block was clicked. Non-members fade as before.
                const renderSelectedStyle = isSelected && !groupHighlight;
                const borderColor = renderSelectedStyle ? '#f59e0b' : color;
                const bgColor = renderSelectedStyle ? 'rgba(245, 158, 11, 0.15)' : isHovered ? `${color}18` : 'transparent';
                const boxShadow = renderSelectedStyle ? '0 0 0 2px rgba(245, 158, 11, 0.3), 0 0 12px rgba(245, 158, 11, 0.2)' : 'none';
                // Members inside the union box render invisibly (no border) but still
                // need pointer events so individual blocks remain clickable inside
                // the highlighted region. Their opacity stays at 1 so the union's
                // amber tint shows through cleanly.
                const memberInGroup = groupHighlight && isSelected;
                return /*#__PURE__*/ _jsx("div", {
                    className: "absolute pointer-events-auto cursor-pointer",
                    style: {
                        left,
                        top,
                        width,
                        height,
                        border: memberInGroup ? 'none' : `2px solid ${borderColor}`,
                        backgroundColor: bgColor,
                        boxShadow,
                        opacity: memberInGroup ? 1 : hasActiveSelection ? isSelected ? 1 : 0.15 : isHovered ? 1 : 0.4,
                        zIndex: isSelected ? 30 : isHovered ? 10 : 1,
                        borderRadius: '2px',
                        transition: 'opacity 150ms, background-color 150ms, box-shadow 150ms'
                    },
                    onClick: (e)=>{
                        e.stopPropagation();
                        onBlockSelect(isSelected ? null : block.id);
                    },
                    onMouseEnter: ()=>setHoveredBlockId(block.id),
                    onMouseLeave: ()=>setHoveredBlockId(null),
                    title: `${block.type} (${Math.round(block.confidence * 100)}% confidence)`,
                    children: (isHovered || isSelected && !groupHighlight) && /*#__PURE__*/ _jsx("div", {
                        "data-testid": "block-label",
                        className: "absolute left-0 top-0 -translate-y-full whitespace-nowrap rounded-t px-1.5 py-0.5 text-[10px] font-semibold text-white",
                        style: {
                            backgroundColor: renderSelectedStyle ? '#f59e0b' : color
                        },
                        children: block.type
                    })
                }, block.id);
            }),
            unionRect && /*#__PURE__*/ _jsx("div", {
                "data-testid": "pws-section-highlight",
                className: "absolute pointer-events-none",
                style: {
                    left: unionRect.left,
                    top: unionRect.top,
                    width: unionRect.width,
                    height: unionRect.height,
                    border: '2px solid #f59e0b',
                    backgroundColor: 'rgba(245, 158, 11, 0.12)',
                    boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.25), 0 0 16px rgba(245, 158, 11, 0.2)',
                    borderRadius: '4px',
                    zIndex: 25,
                    transition: 'opacity 150ms'
                }
            })
        ]
    });
};
